import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBranchList } from "../features/commonSlice";
import axios from "axios";
import {
  Container, Row, Col, Form, Button, Card, Alert
} from "react-bootstrap";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import "../static/css/App.css";

library.add(fas, fab, far);

const stripHtml = (s = "") => s.replace(/(<([^>]+)>)/gi, "").trim();

export default function ContactUs() {
  const dispatch = useDispatch();
  const { branchList } = useSelector((state) => state.common);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [datas, setDatas] = useState({
    name: "",
    email: "",
    mobile: "",
    location: "",
    enquiryFrom: "", // <-- empty by default (NOT 1)
    message: ""
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  // ---------- validation ----------
  const validators = {
    name: (v) => (!v.trim() ? "Name is required" : ""),
    email: (v) => {
      if (!v.trim()) return "Email is required";
      // simple email pattern
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      return re.test(v) ? "" : "Enter a valid email";
    },
    mobile: (v) => {
      if (!v.trim()) return "Mobile is required";
      // India 10-digit starting 6-9; adjust to your needs
      const re = /^[6-9]\d{9}$/;
      return re.test(v) ? "" : "Enter a valid 10-digit mobile";
    },
    location: (v) => (!v.trim() ? "Location is required" : ""),
    enquiryFrom: (v) =>
      v === "" || v === "0" ? "Inquiry For is required" : "",
    message: (v) =>
      stripHtml(v).length === 0 ? "Message is required" : ""
  };

  const validateField = (field, value) => {
    const validator = validators[field];
    return validator ? validator(value) : "";
  };

  const validateAll = (state) => {
    const nextErrors = {};
    Object.keys(validators).forEach((k) => {
      const msg = validateField(k, state[k]);
      if (msg) nextErrors[k] = msg;
    });
    return nextErrors;
  };

  // ---------- handlers ----------
  const handleBlur = (field) => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors((e) => ({
      ...e,
      [field]: validateField(field, datas[field])
    }));
  };

  const handleString = (e) => {
    const { name, value } = e.target;
    // letters, numbers, spaces, hyphen, parentheses
    const regex = /^[0-9a-zA-Z()\- ]+$/;
    if (value === "" || regex.test(value)) {
      setDatas((d) => {
        const next = { ...d, [name]: value };
        if (touched[name]) {
          setErrors((er) => ({ ...er, [name]: validateField(name, next[name]) }));
        }
        return next;
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDatas((d) => {
      const next = { ...d, [name]: value };
      if (touched[name]) {
        setErrors((er) => ({ ...er, [name]: validateField(name, next[name]) }));
      }
      return next;
    });
  };

  const handleMessageChange = (e) => {
    const cleaned = stripHtml(e.target.value);
    setDatas((d) => {
      const next = { ...d, message: cleaned };
      if (touched.message) {
        setErrors((er) => ({
          ...er,
          message: validateField("message", next.message)
        }));
      }
      return next;
    });
  };

  const contactSubmit = async () => {
    // validate everything on submit
    const nextErrors = validateAll(datas);
    setErrors(nextErrors);
    setTouched({
      name: true,
      email: true,
      mobile: true,
      location: true,
      enquiryFrom: true,
      message: true
    });

    if (Object.keys(nextErrors).length > 0) {
      setSuccess("");
      setError("Please correct the highlighted fields.");
      return;
    }

    try {
      setError("");
      setSuccess("");
      const brchUrl = `${process.env.REACT_APP_BASE_URL}/contact/create`;
      const payload = {
        ...datas,
        enquiryFrom: String(datas.enquiryFrom || "0")
      };
      const response = await axios.post(brchUrl, payload);
      if (response.data.status === "SUCCESS") {
        setSuccess("Inquiry sent successfully");
        setDatas({
          name: "",
          email: "",
          mobile: "",
          location: "",
          enquiryFrom: "",
          message: ""
        });
        setTouched({});
        setErrors({});
      } else {
        setError(response.data.message || "Failed to send inquiry.");
      }
    } catch {
      setError("Something went wrong. Please try again later.");
    }
  };

  // ---------- effects ----------
  useEffect(() => {
    dispatch(fetchBranchList());
    window.scrollTo(0, 0);
    setError("");
  }, [dispatch]);

  useEffect(() => {
    setError("");
  }, [datas]);

  // ---------- render ----------
  return (
    <div>
      <Container fluid="xl" className="px-md-5 px-3 px-xl-3">
        <Row><h2>Contact Us</h2></Row>

        <Row>
          {error && (
            <Alert variant="danger" className="text-center">
              <p className="mb-0">{error}</p>
            </Alert>
          )}
          {success && (
            <Alert variant="success" className="text-center">
              <p className="mb-0">{success}</p>
            </Alert>
          )}
        </Row>

        <Row>
          <Form noValidate>
            {/* Name */}
            <Form.Group as={Row} className="mb-3 justify-content-center">
              <Form.Label column sm={1}><b>Name</b></Form.Label>
              <Col sm={4}>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={datas.name}
                  onChange={handleString}
                  onBlur={() => handleBlur("name")}
                  isInvalid={touched.name && !!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            {/* Email */}
            <Form.Group as={Row} className="mb-3 justify-content-center">
              <Form.Label column sm={1}><b>Email</b></Form.Label>
              <Col sm={4}>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={datas.email}
                  onChange={handleChange}
                  onBlur={() => handleBlur("email")}
                  isInvalid={touched.email && !!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            {/* Mobile */}
            <Form.Group as={Row} className="mb-3 justify-content-center">
              <Form.Label column sm={1}><b>Mobile No</b></Form.Label>
              <Col sm={4}>
                <Form.Control
                  type="tel"
                  name="mobile"
                  placeholder="10-digit mobile"
                  value={datas.mobile}
                  onChange={handleChange}
                  onBlur={() => handleBlur("mobile")}
                  maxLength={10}
                  isInvalid={touched.mobile && !!errors.mobile}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.mobile}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            {/* Location */}
            <Form.Group as={Row} className="mb-3 justify-content-center">
              <Form.Label column sm={1}><b>Location</b></Form.Label>
              <Col sm={4}>
                <Form.Control
                  type="text"
                  name="location"
                  placeholder="Location"
                  value={datas.location}
                  onChange={handleString}
                  onBlur={() => handleBlur("location")}
                  isInvalid={touched.location && !!errors.location}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.location}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            {/* Inquiry For */}
            <Form.Group as={Row} className="mb-3 justify-content-center">
              <Form.Label column sm={1}><b>Inquiry For</b></Form.Label>
              <Col sm={4}>
                <Form.Select
                  name="enquiryFrom"
                  value={datas.enquiryFrom}
                  onChange={handleChange}
                  onBlur={() => handleBlur("enquiryFrom")}
                  isInvalid={touched.enquiryFrom && !!errors.enquiryFrom}
                >
                  <option value="">Select Type</option>
                  <option value="1">General Inquiry</option>
                  <option value="2">Product Inquiry</option>
                  <option value="3">Complaints</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.enquiryFrom}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            {/* Message */}
            <Form.Group as={Row} className="mb-3 justify-content-center">
              <Form.Label column sm={1}><b>Message</b></Form.Label>
              <Col sm={4}>
                <Form.Control
                  as="textarea"
                  name="message"
                  rows={5}
                  placeholder="Your Query"
                  value={datas.message}
                  onChange={handleMessageChange}
                  onBlur={() => handleBlur("message")}
                  isInvalid={touched.message && !!errors.message}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Col sm={{ span: 6, offset: 6 }}>
                <Button type="button" onClick={contactSubmit}>
                  Submit
                </Button>
              </Col>
            </Form.Group>
          </Form>
        </Row>

        <Row><h2>Our Branches</h2></Row>
        <Row>
          {branchList?.map((bl, index) => (
            <Col sm="3" className="my-3" key={bl.id ?? index}>
              <Card className="h-100">
                <Card.Body className="p-2 lh-lg text-wrap">
                  <p className="text-primary mb-0">
                    <strong>{index + 1}. {bl.branchName}</strong>
                  </p>
                  {bl.address}
                  <br />
                  {/* {bl.areaName},{bl.city}-{bl.pincode}
                  <br /> */}
                  <strong>Mobile No:</strong> {bl.mobile}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
}
